/**
 * Gender Color Utilities
 * 
 * Provides consistent gender-based color schemes across the application.
 * Centralizes gender color logic to ensure consistency and easy maintenance.
 */

/**
 * Get text color classes for gender
 * @param {string} gender - 'male', 'female', or other
 * @returns {string} Tailwind CSS text color classes
 */
export const getGenderTextColor = (gender) => {
  switch (gender?.toLowerCase()) {
    case 'female':
      return 'text-female';
    case 'male':
      return 'text-male';
    default:
      return 'text-text-primary';
  }
};

/**
 * Get background and text color classes for gender badges
 * @param {string} gender - 'male', 'female', or other
 * @returns {string} Tailwind CSS background and text color classes
 */
export const getGenderBadgeColors = (gender) => {
  switch (gender?.toLowerCase()) {
    case 'female':
      return 'bg-pink-100 text-pink-700';
    case 'male':
      return 'bg-blue-100 text-blue-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

/**
 * Get gender symbol
 * @param {string} gender - 'male', 'female', or other
 * @returns {string} Gender symbol (♀, ♂, or ?)
 */
export const getGenderSymbol = (gender) => {
  switch (gender?.toLowerCase()) {
    case 'female':
      return '♀';
    case 'male':
      return '♂';
    default:
      return '?';
  }
};

/**
 * Get complete gender badge classes (background, text, and border)
 * @param {string} gender - 'male', 'female', or other
 * @returns {string} Complete Tailwind CSS classes for gender badge
 */
export const getGenderBadgeClasses = (gender) => {
  const colors = getGenderBadgeColors(gender);
  return `${colors} border border-current`;
};

/**
 * Get gender indicator dot color
 * @param {string} gender - 'male', 'female', or other
 * @returns {string} Tailwind CSS background color for indicator dot
 */
export const getGenderDotColor = (gender) => {
  switch (gender?.toLowerCase()) {
    case 'female':
      return 'bg-pink-400';
    case 'male':
      return 'bg-blue-400';
    default:
      return 'bg-gray-400';
  }
};

/**
 * Get avatar background color for gender
 * Use this for player avatar circles throughout the app
 * @param {string} gender - 'male', 'female', or other
 * @returns {string} Tailwind CSS background color for avatar
 */
export const getGenderAvatarBg = (gender) => {
  switch (gender?.toLowerCase()) {
    case 'female':
      return 'bg-female';
    case 'male':
      return 'bg-primary';
    default:
      return 'bg-accent';
  }
};
